import { useEffect, useMemo, useRef, useState } from 'react';
import { apiClient } from '@services/api';
import './VisualCartScanner.scss';

// Visual Cart: the guest photographs their basket at a self-checkout station and the server asks
// Koridor which menu items are in it. Every scan sends ALL the basket's photos and replaces the last
// result. Photos are grouped: a group is one item (or a few) from several angles, counted once; the
// groups are added up. "New item" starts a group, "Another angle" adds to one.

export interface ScannerMenuItem {
  id: number;
  name: string;
  price?: number;
  imagePath?: string;
  stockQuantity?: number;
}

export interface BasketPhoto {
  id: string;
  /** A photo from the guest's phone. */
  blob?: Blob;
  /** A photo the counter camera took for this guest (sent by id, never re-uploaded). */
  captureId?: string;
  url: string;
  /** Photos with the same group show the same item(s) from different angles. */
  group: number;
}

export interface CounterCamera { id: string; name: string }

interface Box { photoIndex: number; x: number; y: number; width: number; height: number }

interface ScanResult {
  scanId: string;
  success: boolean;
  errorCode?: 'not_enabled' | 'unreadable' | 'timeout' | 'failed';
  error?: string;
  items: { menuItemId: number; quantity: number; confidence?: string; positions?: Box[] }[];
  possible: { quantity: number; note?: string; candidateMenuItemIds: number[]; positions?: Box[] }[];
  unrecognized: { description: string; quantity: number; positions?: Box[] }[];
}

interface Line { menuItemId: number; quantity: number; confidence?: string }

interface Props {
  hotelSlug: string;
  stationId: number;
  items: ScannerMenuItem[];
  currencySymbol: string;
  photos: BasketPhoto[];
  onPhotosChange: (photos: BasketPhoto[]) => void;
  /** The station's counter camera, when it has one online. */
  counterCamera?: CounterCamera | null;
  /** False when the station takes counter photos only (and the counter camera is up). */
  allowPhonePhotos?: boolean;
  onApply: (lines: { menuItemId: number; quantity: number }[], scanId: string) => void;
  onClose: () => void;
  t: (key: string) => string;
}

const MAX_PHOTOS = 10;
const MAX_EDGE = 1600;

// Translation with an English fallback, as the rest of the guest app does it.
const tr = (t: Props['t'], key: string, fallback: string) => {
  const v = t(key);
  return v && v !== key ? v : fallback;
};

/** Shrink a phone photo (often 4000px+, several MB) to something quick to upload. */
async function downscale(file: File): Promise<Blob> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();
    const blob = await new Promise<Blob | null>(res => canvas.toBlob(res, 'image/jpeg', 0.85));
    return blob ?? file;
  } catch {
    // Formats the browser can't decode (e.g. HEIC outside Safari) go up as they are.
    return file;
  }
}

export default function VisualCartScanner({
  hotelSlug, stationId, items, currencySymbol, photos, onPhotosChange, counterCamera, allowPhonePhotos = true, onApply, onClose, t,
}: Props) {
  const [stage, setStage] = useState<'capture' | 'scanning' | 'result'>('capture');
  const [result, setResult] = useState<ScanResult | null>(null);
  const [lines, setLines] = useState<Line[]>([]);
  // Which candidate the guest picked for each "possible" entry (null = none of these)
  const [picks, setPicks] = useState<Record<number, number | null>>({});
  const [error, setError] = useState<string | null>(null);
  // Counter camera: 3-2-1 countdown, then waiting for the photo
  const [counterStep, setCounterStep] = useState<number | 'waiting' | null>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  // The group the next phone photo joins ("Another angle"); null = a new item
  const angleFor = useRef<number | null>(null);
  const galleryInput = useRef<HTMLInputElement>(null);

  const byId = useMemo(() => new Map(items.map(i => [i.id, i])), [items]);
  const sym = currencySymbol;

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const nextGroup = () => photos.reduce((m, p) => Math.max(m, p.group), -1) + 1;

  // Groups in the order they were started, each with its photos
  const groups = useMemo(() => {
    const map = new Map<number, BasketPhoto[]>();
    photos.forEach(p => map.set(p.group, [...(map.get(p.group) ?? []), p]));
    return Array.from(map.values());
  }, [photos]);

  const addFiles = async (files: FileList | null) => {
    const joinGroup = angleFor.current;
    angleFor.current = null;
    if (!files || files.length === 0) return;
    const room = MAX_PHOTOS - photos.length;
    const picked = Array.from(files).slice(0, room);
    const added: BasketPhoto[] = [];
    let group = nextGroup();
    for (const f of picked) {
      const blob = await downscale(f);
      // An extra angle joins its item; otherwise each photo is a new item
      added.push({ id: `${Date.now()}-${Math.random()}`, blob, url: URL.createObjectURL(blob), group: joinGroup ?? group++ });
    }
    onPhotosChange([...photos, ...added]);
    setError(null);
  };

  const takeAngle = (group: number) => {
    angleFor.current = group;
    cameraInput.current?.click();
  };

  const removePhoto = (id: string) => {
    const p = photos.find(x => x.id === id);
    if (p?.blob) URL.revokeObjectURL(p.url);
    onPhotosChange(photos.filter(x => x.id !== id));
  };

  // The counter camera photographs whatever is on the counter — a short countdown gives the guest
  // time to put everything down, then the photo is taken and shown like any other.
  const captureCounter = async () => {
    if (!counterCamera || photos.length >= MAX_PHOTOS) return;
    setError(null);
    for (const n of [3, 2, 1]) {
      setCounterStep(n);
      await new Promise(r => setTimeout(r, 1000));
    }
    setCounterStep('waiting');
    const base = `/api/v1/${hotelSlug}/stations/${stationId}/visual-cart/counter`;
    try {
      const start = await apiClient.post<{ commandId: string }>(`${base}/${counterCamera.id}/capture`, {});
      for (let i = 0; i < 20; i++) {
        await new Promise(r => setTimeout(r, 1000));
        const res = await apiClient.get<{ status: string; captureId?: string; photoUrl?: string; error?: string }>(
          `${base}/commands/${start.data.commandId}`);
        const st = res.data;
        if (st.status === 'done' && st.captureId && st.photoUrl) {
          onPhotosChange([...photos, { id: st.captureId, captureId: st.captureId, url: st.photoUrl, group: nextGroup() }]);
          setCounterStep(null);
          return;
        }
        if (st.status === 'failed' || st.status === 'expired') break;
      }
      setError(tr(t, 'visual_cart.counterFailed', "The counter camera didn't answer. Try again, or use your phone's camera."));
    } catch (err: unknown) {
      const e = err as { response?: { data?: { Error?: string; error?: string } } };
      setError(e.response?.data?.Error || e.response?.data?.error
        || tr(t, 'visual_cart.counterFailed', "The counter camera didn't answer. Try again, or use your phone's camera."));
    }
    setCounterStep(null);
  };

  const scan = async () => {
    if (photos.length === 0) return;
    setStage('scanning');
    setError(null);
    try {
      const form = new FormData();
      // The server reads phone photos first, then counter photos — groups are indexes in that order.
      const ordered = [...photos.filter(p => p.blob), ...photos.filter(p => !p.blob && p.captureId)];
      ordered.forEach((p, i) => {
        if (p.blob) form.append('photos', p.blob, `basket-${i}.jpg`);
        else if (p.captureId) form.append('captureIds', p.captureId);
      });
      const byGroup = new Map<number, number[]>();
      ordered.forEach((p, i) => byGroup.set(p.group, [...(byGroup.get(p.group) ?? []), i]));
      form.append('groups', JSON.stringify(Array.from(byGroup.values())));
      const res = await apiClient.post<ScanResult>(
        `/api/v1/${hotelSlug}/stations/${stationId}/visual-cart/scan`, form);
      const r = res.data;
      if (!r.success) {
        setError(
          r.errorCode === 'unreadable'
            ? tr(t, 'visual_cart.unreadable', "We couldn't make out the photo. Lay the items flat, labels up, in good light, and try again.")
            : r.errorCode === 'not_enabled'
              ? tr(t, 'visual_cart.notEnabled', 'Scanning isn’t available here yet. Please add your items from the menu.')
              : r.errorCode === 'timeout'
                ? tr(t, 'visual_cart.timeout', 'That took too long. Please try again.')
                : tr(t, 'visual_cart.failed', 'Something went wrong while scanning. Please try again, or add your items from the menu.'));
        setStage('capture');
        return;
      }
      setResult(r);
      setLines(r.items
        .filter(i => byId.has(i.menuItemId))
        .map(i => ({ menuItemId: i.menuItemId, quantity: i.quantity, confidence: i.confidence })));
      setPicks({});
      setStage('result');
    } catch (err: unknown) {
      const e = err as { response?: { data?: { Error?: string; error?: string } } };
      setError(e.response?.data?.Error || e.response?.data?.error
        || tr(t, 'visual_cart.failed', 'Something went wrong while scanning. Please try again, or add your items from the menu.'));
      setStage('capture');
    }
  };

  const setQty = (menuItemId: number, qty: number) => {
    setLines(prev => qty <= 0
      ? prev.filter(l => l.menuItemId !== menuItemId)
      : prev.map(l => l.menuItemId === menuItemId ? { ...l, quantity: qty } : l));
  };

  // Final basket = recognised lines + the candidates the guest picked for the unsure ones
  const finalLines = useMemo(() => {
    const merged = new Map<number, number>();
    lines.forEach(l => merged.set(l.menuItemId, (merged.get(l.menuItemId) ?? 0) + l.quantity));
    result?.possible.forEach((p, idx) => {
      const pick = picks[idx];
      if (pick != null) merged.set(pick, (merged.get(pick) ?? 0) + p.quantity);
    });
    return Array.from(merged, ([menuItemId, quantity]) => ({ menuItemId, quantity }));
  }, [lines, picks, result]);

  const total = finalLines.reduce((s, l) => s + (byId.get(l.menuItemId)?.price ?? 0) * l.quantity, 0);
  const unanswered = (result?.possible ?? []).filter((_, idx) => !(idx in picks)).length;

  // Numbered pins on the photos, when Koridor sends positions (opt-in on their side).
  const pins = useMemo(() => {
    if (!result) return [] as { n: number; box: Box; tone: 'ok' | 'ask' | 'unknown' }[];
    const list: { n: number; box: Box; tone: 'ok' | 'ask' | 'unknown' }[] = [];
    let n = 0;
    result.items.forEach(i => { n++; i.positions?.forEach(box => list.push({ n, box, tone: 'ok' })); });
    result.possible.forEach(p => { n++; p.positions?.forEach(box => list.push({ n, box, tone: 'ask' })); });
    result.unrecognized.forEach(u => { n++; u.positions?.forEach(box => list.push({ n, box, tone: 'unknown' })); });
    return list;
  }, [result]);

  // Box positions point at photos in the order they were sent (phone photos first)
  const sentIndex = useMemo(() => {
    const ordered = [...photos.filter(p => p.blob), ...photos.filter(p => !p.blob && p.captureId)];
    return new Map(ordered.map((p, i) => [p.id, i]));
  }, [photos]);

  const renderPhoto = (p: BasketPhoto, removable: boolean) => (
    <div key={p.id} className="vc-photo">
      <img src={p.url} alt="" />
      {pins.filter(pin => pin.box.photoIndex === sentIndex.get(p.id)).map((pin, k) => (
        <span
          key={k}
          className={`vc-pin ${pin.tone}`}
          style={{ left: `${(pin.box.x + pin.box.width / 2) * 100}%`, top: `${(pin.box.y + pin.box.height / 2) * 100}%` }}
        >{pin.n}</span>
      ))}
      {removable && (
        <button className="vc-photo-remove" onClick={() => removePhoto(p.id)} aria-label={tr(t, 'common.remove', 'Remove')}>×</button>
      )}
    </div>
  );

  let lineNumber = 0;

  return (
    <div className="vc-overlay" role="dialog" aria-modal="true">
      <div className="vc-sheet">
        <div className="vc-header">
          <h2>📷 {tr(t, 'visual_cart.title', 'Scan your items')}</h2>
          <button className="vc-close" onClick={onClose} aria-label={tr(t, 'common.close', 'Close')}>×</button>
        </div>

        <input ref={cameraInput} type="file" accept="image/*" capture="environment" hidden
          onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />
        <input ref={galleryInput} type="file" accept="image/*" multiple hidden
          onChange={e => { addFiles(e.target.files); e.target.value = ''; }} />

        {stage === 'capture' && (
          <div className="vc-body">
            <p className="vc-tip">
              {tr(t, 'visual_cart.tipGroups', 'Photograph your items, labels up, in good light — one item or several per photo. If something is hidden, add another angle of that photo: angles are counted once.')}
            </p>

            {groups.map((g, gi) => (
              <div key={g[0].group} className="vc-group">
                <div className="vc-group-head">
                  <span>{tr(t, 'visual_cart.photoGroup', 'Photo')} {gi + 1}
                    {g.length > 1 && <small> · {g.length} {tr(t, 'visual_cart.angles', 'angles, counted once')}</small>}
                  </span>
                  {photos.length < MAX_PHOTOS && allowPhonePhotos && (
                    <button className="vc-link" onClick={() => takeAngle(g[0].group)} disabled={counterStep !== null}>
                      ＋ {tr(t, 'visual_cart.anotherAngle', 'Another angle')}
                    </button>
                  )}
                </div>
                <div className="vc-photos">{g.map(p => renderPhoto(p, true))}</div>
              </div>
            ))}

            {error && <div className="vc-error">⚠️ {error}</div>}

            {counterStep !== null && (
              <div className="vc-countdown">
                {counterStep === 'waiting'
                  ? <><div className="vc-spinner" /><span>{tr(t, 'visual_cart.counterTaking', 'Taking the photo…')}</span></>
                  : <><strong>{counterStep}</strong><span>{tr(t, 'visual_cart.counterHold', 'Put your items on the counter')}</span></>}
              </div>
            )}

            <div className="vc-actions">
              {photos.length < MAX_PHOTOS && counterCamera && (
                <button className="vc-btn primary" onClick={captureCounter} disabled={counterStep !== null}>
                  📷 {photos.some(p => p.captureId)
                    ? tr(t, 'visual_cart.counterAgain', 'Photograph the counter again')
                    : tr(t, 'visual_cart.counterPhoto', 'Photograph the counter')}
                </button>
              )}
              {photos.length < MAX_PHOTOS && allowPhonePhotos && (
                <>
                  <button className={`vc-btn ${counterCamera ? 'ghost' : 'primary'}`} onClick={() => { angleFor.current = null; cameraInput.current?.click(); }} disabled={counterStep !== null}>
                    📱 {photos.length === 0 ? tr(t, 'visual_cart.takePhoto', 'Take a photo') : tr(t, 'visual_cart.newItemPhoto', 'Photo of other items')}
                  </button>
                  <button className="vc-btn ghost" onClick={() => { angleFor.current = null; galleryInput.current?.click(); }} disabled={counterStep !== null}>
                    🖼️ {tr(t, 'visual_cart.fromGallery', 'Choose from gallery')}
                  </button>
                </>
              )}
              {photos.length > 0 && (
                <button className="vc-btn success" onClick={scan} disabled={counterStep !== null}>
                  🔍 {tr(t, 'visual_cart.check', 'Check my items')} ({photos.length})
                </button>
              )}
            </div>
          </div>
        )}

        {stage === 'scanning' && (
          <div className="vc-body vc-scanning">
            <div className="vc-spinner" />
            <h3>{tr(t, 'visual_cart.scanning', 'Checking your items…')}</h3>
            <p>{tr(t, 'visual_cart.scanningNote', 'This takes a few seconds.')}</p>
          </div>
        )}

        {stage === 'result' && result && (
          <div className="vc-body">
            {pins.length > 0 && <div className="vc-photos">{photos.map(p => renderPhoto(p, false))}</div>}

            {lines.length === 0 && result.possible.length === 0 && (
              <div className="vc-empty">
                {tr(t, 'visual_cart.nothingFound', "We didn't find any of this station's items in the photo.")}
              </div>
            )}

            {lines.length > 0 && (
              <section className="vc-section">
                <h3>✅ {tr(t, 'visual_cart.found', 'We found')}</h3>
                {result.items.filter(i => byId.has(i.menuItemId)).map(i => {
                  lineNumber++;
                  const line = lines.find(l => l.menuItemId === i.menuItemId);
                  const mi = byId.get(i.menuItemId)!;
                  if (!line) return null;
                  return (
                    <div key={i.menuItemId} className={`vc-line ${line.confidence === 'high' ? '' : 'check'}`}>
                      {pins.length > 0 && <span className="vc-num ok">{lineNumber}</span>}
                      {mi.imagePath && <img src={mi.imagePath} alt="" className="vc-thumb" />}
                      <div className="vc-line-info">
                        <span className="vc-line-name">{mi.name}</span>
                        {line.confidence !== 'high' && (
                          <span className="vc-line-hint">{tr(t, 'visual_cart.pleaseCheck', 'Please check this one')}</span>
                        )}
                        {i.positions && i.positions.length < line.quantity && pins.length > 0 && (
                          <span className="vc-line-hint">
                            +{line.quantity - i.positions.length} {tr(t, 'visual_cart.notMarked', 'not marked on the photo')}
                          </span>
                        )}
                      </div>
                      <div className="vc-qty">
                        <button onClick={() => setQty(i.menuItemId, line.quantity - 1)}>−</button>
                        <span>{line.quantity}</span>
                        <button
                          onClick={() => setQty(i.menuItemId, line.quantity + 1)}
                          disabled={mi.stockQuantity != null && line.quantity >= mi.stockQuantity}
                        >+</button>
                      </div>
                      {mi.price != null && mi.price > 0 && (
                        <span className="vc-price">{sym}{(mi.price * line.quantity).toFixed(2)}</span>
                      )}
                    </div>
                  );
                })}
              </section>
            )}

            {result.possible.length > 0 && (
              <section className="vc-section">
                <h3>❓ {tr(t, 'visual_cart.whichOne', 'Which one is this?')}</h3>
                {result.possible.map((p, idx) => {
                  lineNumber++;
                  return (
                    <div key={idx} className="vc-possible">
                      <div className="vc-possible-head">
                        {pins.length > 0 && <span className="vc-num ask">{lineNumber}</span>}
                        <span>{p.quantity > 1 ? `${p.quantity}× ` : ''}{p.note || tr(t, 'visual_cart.notSure', "We weren't sure")}</span>
                      </div>
                      <div className="vc-candidates">
                        {p.candidateMenuItemIds.filter(id => byId.has(id)).map(id => {
                          const mi = byId.get(id)!;
                          return (
                            <button
                              key={id}
                              className={`vc-candidate ${picks[idx] === id ? 'picked' : ''}`}
                              onClick={() => setPicks(prev => ({ ...prev, [idx]: id }))}
                            >
                              {mi.imagePath && <img src={mi.imagePath} alt="" />}
                              <span>{mi.name}</span>
                              {mi.price != null && mi.price > 0 && <small>{sym}{mi.price.toFixed(2)}</small>}
                            </button>
                          );
                        })}
                        <button
                          className={`vc-candidate none ${picks[idx] === null ? 'picked' : ''}`}
                          onClick={() => setPicks(prev => ({ ...prev, [idx]: null }))}
                        >
                          <span>{tr(t, 'visual_cart.noneOfThese', 'None of these')}</span>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </section>
            )}

            {result.unrecognized.length > 0 && (
              <section className="vc-section">
                <h3>⚠️ {tr(t, 'visual_cart.notOnMenu', 'Not on this menu')}</h3>
                {result.unrecognized.map((u, idx) => {
                  lineNumber++;
                  return (
                    <div key={idx} className="vc-unknown">
                      {pins.length > 0 && <span className="vc-num unknown">{lineNumber}</span>}
                      <span>{u.quantity > 1 ? `${u.quantity}× ` : ''}{u.description || tr(t, 'visual_cart.unknownItem', 'An item we couldn’t match')}</span>
                    </div>
                  );
                })}
                <p className="vc-hint">
                  {tr(t, 'visual_cart.notOnMenuHint', 'Put it back, retake the photo, or add it from the menu if you can find it.')}
                </p>
              </section>
            )}

            <div className="vc-actions">
              {finalLines.length > 0 && (
                <button
                  className="vc-btn success"
                  disabled={unanswered > 0}
                  onClick={() => onApply(finalLines, result.scanId)}
                >
                  {unanswered > 0
                    ? tr(t, 'visual_cart.answerFirst', 'Choose an option above first')
                    : `🛒 ${tr(t, 'visual_cart.useItems', 'Use these items')}${total > 0 ? ` · ${sym}${total.toFixed(2)}` : ''}`}
                </button>
              )}
              <button className="vc-btn ghost" onClick={() => { setStage('capture'); setResult(null); }}>
                📷 {tr(t, 'visual_cart.addOrRetake', 'Add a photo or retake')}
              </button>
              <p className="vc-hint">{tr(t, 'visual_cart.replacesCart', 'Using these items replaces what is in your cart.')}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
